import { useEffect, useMemo, useRef, useState } from 'react';

import type { MatchedLocation } from '../lib/api.js';
import { MAP_STYLE_ID, loadGoogleMaps, mapsConfigured } from '../lib/google-maps.js';

/**
 * Where the matched locations are.
 *
 * GOOGLE MAPS, THE SAME ONE THE REST OF THE PRODUCT USES. An earlier version of
 * this file plotted the points on a bare Mercator scatter with a docblock
 * claiming "this product ships no map library anywhere" — which was simply
 * false: `frontend-client` has had Google Maps behind one Blade component since
 * search results shipped, and a producer who has just been looking at listings
 * on the public site would have met a different, worse map here. The loader in
 * `lib/google-maps.ts` mirrors that component, down to the map style id.
 *
 * NUMBERED, AND THE NUMBERS MATCH THE LIST. A pin a producer cannot tie to a
 * name is decoration; the badge on the marker is the badge in the list beside
 * it, and clicking either moves the map.
 *
 * PINS ARE ABSENT, NOT HIDDEN. A point of interest's coordinates are jittered
 * before they reach a producer and its exact point is deliberately never sent
 * (ADR 0012), so it has nothing to plot. The caption says how many were left
 * off rather than quietly under-reporting the count.
 */

interface Mappable {
  readonly location: MatchedLocation;
  readonly index: number;
  readonly latitude: number;
  readonly longitude: number;
}

/** Only what this file calls, so the package needs no Maps type dependency. */
interface MapsApi {
  Map: new (el: HTMLElement, options: Record<string, unknown>) => MapInstance;
  LatLngBounds: new () => { extend: (p: { lat: number; lng: number }) => void };
  marker?: {
    AdvancedMarkerElement: new (options: Record<string, unknown>) => {
      addListener: (event: string, handler: () => void) => void;
    };
  };
}

interface MapInstance {
  fitBounds: (bounds: unknown, padding?: number) => void;
  setCenter: (position: { lat: number; lng: number }) => void;
  setZoom: (zoom: number) => void;
}

export function LocationMap({
  locations,
  onFocus,
  variant = 'split',
}: {
  locations: readonly MatchedLocation[];
  onFocus?: (location: MatchedLocation) => void;
  /**
   * `split` is the Locations tab's: the map beside its numbered list.
   * `single` is the map alone, shorter — a location's own detail panel, where
   * the list beside it would be a list of one.
   */
  variant?: 'split' | 'single';
}) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<MapInstance | null>(null);
  const [failed, setFailed] = useState(false);

  const mappable = useMemo<Mappable[]>(
    () =>
      locations
        .map((location, i) => ({ location, index: i + 1 }))
        .filter(
          (entry): entry is Mappable =>
            // TWO CONDITIONS, AND THE FIRST IS THE ONE THAT MATTERS. A point of
            // interest's exact position is deliberately never sent to a
            // producer (ADR 0012), so the server already nulls its coordinates
            // and the null check alone would exclude it. The explicit check is
            // here because a privacy boundary must not depend on one service
            // remembering — if a future payload ever carries them, this file
            // still does not plot them.
            entry.location.sourceType !== 'POI' &&
            entry.location.latitude !== null &&
            entry.location.longitude !== null,
        )
        .map((entry) => ({
          ...entry,
          latitude: entry.location.latitude as unknown as number,
          longitude: entry.location.longitude as unknown as number,
        })),
    [locations],
  );

  const withheld = locations.length - mappable.length;

  useEffect(() => {
    if (!mapsConfigured() || mappable.length === 0) return;

    let cancelled = false;

    loadGoogleMaps()
      .then(() => {
        if (cancelled || !container.current) return;

        const maps = (window.google as { maps?: MapsApi } | undefined)?.maps;
        // `google.maps` is truthy before its constructors attach — always test
        // the constructor itself (frontend-client/CLAUDE.md, trap 1).
        if (!maps?.Map) throw new Error('google.maps.Map unavailable');

        const instance = new maps.Map(container.current, {
          mapId: MAP_STYLE_ID,
          zoom: 5,
          center: { lat: mappable[0]?.latitude ?? 0, lng: mappable[0]?.longitude ?? 0 },
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        map.current = instance;

        const bounds = new maps.LatLngBounds();
        for (const entry of mappable) {
          bounds.extend({ lat: entry.latitude, lng: entry.longitude });

          const Advanced = maps.marker?.AdvancedMarkerElement;
          if (!Advanced) continue;

          const badge = document.createElement('div');
          badge.textContent = String(entry.index);
          badge.setAttribute('data-testid', `map-pin-${String(entry.location.sourceId)}`);
          badge.style.cssText =
            'background:var(--color-accent,#d0342c);color:#fff;font-weight:700;font-size:12px;' +
            'width:24px;height:24px;display:flex;align-items:center;justify-content:center;' +
            'border-radius:2px;box-shadow:0 1px 4px rgba(0,0,0,.35)';

          const marker = new Advanced({
            map: instance,
            position: { lat: entry.latitude, lng: entry.longitude },
            content: badge,
            title: entry.location.title,
          });

          marker.addListener('click', () => {
            onFocus?.(entry.location);
          });
        }

        // One point has no bounds worth fitting: fitBounds on it zooms to the
        // street, past anything a producer can place. A town-level view instead.
        const only = mappable.length === 1 ? mappable[0] : undefined;
        if (only === undefined) {
          instance.fitBounds(bounds, 48);
        } else {
          instance.setCenter({ lat: only.latitude, lng: only.longitude });
          instance.setZoom(13);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Logged, never swallowed: a silent catch is how the public site spent a
        // release rendering "Map unavailable" with the cause invisible.
        console.error('[matched-map] could not start the map:', error);
        setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [mappable, onFocus]);

  const unavailable = !mapsConfigured() || failed || mappable.length === 0;
  const height = variant === 'single' ? 260 : 420;

  return (
    <div
      className={variant === 'single' ? undefined : 'split-2'}
      data-testid="location-map"
      data-variant={variant}
      style={{ gap: 16, marginTop: 16 }}
    >
      <div style={{ minWidth: 0 }}>
        {unavailable ? (
          <div
            data-testid="location-map-unavailable"
            style={{
              height,
              display: 'grid',
              placeItems: 'center',
              padding: 24,
              textAlign: 'center',
              border: '1px solid var(--color-divider)',
              background: 'var(--color-surface)',
            }}
          >
            <p className="mm" style={{ fontSize: 13, margin: 0, maxWidth: '42ch' }}>
              {/* Three genuinely different reasons, and a producer can act on
                  only one of them — so they are not collapsed into one
                  apology. */}
              {!mapsConfigured()
                ? 'The map is not configured for this environment.'
                : mappable.length === 0
                  ? 'None of these have a position we can show yet.'
                  : 'The map could not be loaded. The list beside it is complete.'}
            </p>
          </div>
        ) : (
          <div
            ref={container}
            data-testid="location-map-canvas"
            style={{ height, border: '1px solid var(--color-divider)' }}
          />
        )}

        {withheld === 0 ? null : (
          <p className="mm" data-testid="map-withheld" style={{ fontSize: 11, margin: '8px 0 0' }}>
            {withheld} scout find{withheld === 1 ? '' : 's'} {withheld === 1 ? 'is' : 'are'} not on
            the map — a point of interest keeps its exact position until it is brought onto the
            platform.
          </p>
        )}
      </div>

      {variant === 'single' ? null : (
        <ol
          data-testid="map-legend"
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8, minWidth: 0 }}
        >
          {mappable.map((entry) => (
            <li key={`${entry.location.sourceType}:${String(entry.location.sourceId)}`}>
              <button
                type="button"
                data-testid={`map-legend-${String(entry.location.sourceId)}`}
                onClick={() => {
                  map.current?.setCenter({ lat: entry.latitude, lng: entry.longitude });
                  map.current?.setZoom(12);
                  onFocus?.(entry.location);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'baseline',
                  textAlign: 'left',
                  padding: '12px 14px',
                  border: '1px solid var(--color-divider)',
                  background: 'var(--color-bg)',
                  cursor: 'pointer',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    background: 'var(--color-accent)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    minWidth: 22,
                    height: 22,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                >
                  {entry.index}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>
                    {entry.location.title}
                  </span>
                  <span className="mm" style={{ fontSize: 12 }}>
                    {[entry.location.city, entry.location.countryIso].filter(Boolean).join(', ')}
                    {coverageOf(entry.location) === null
                      ? ''
                      : ` · ${String(coverageOf(entry.location))}%`}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * The card's own figure, repeated here so the two never disagree.
 *
 * COVERAGE, not a fit score — how many of the requirement's must-haves this
 * listing evidences. `null` when nothing was asked of it, because "0%" would
 * read as a failed assessment rather than an absent one.
 */
function coverageOf(location: MatchedLocation): number | null {
  const first = location.requirements[0];
  if (!first || !first.assessed || first.coverage.total === 0) return null;
  return Math.round((first.coverage.verified / first.coverage.total) * 100);
}
